import React, { useState, useEffect, useRef } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import {
  collection,
  addDoc,
  getDoc,
  updateDoc,
  doc,
  serverTimestamp,
  orderBy,
  query,
  onSnapshot,
  increment,
  arrayRemove,
  arrayUnion,
  where,
} from "firebase/firestore";
import { db, storage, auth } from "../firebase.config";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { Button, useColorModeValue, Icon, Text, Box, VStack, Heading, Avatar, Container, Flex, HStack, SimpleGrid, Image } from "@chakra-ui/react";
import UserContext from "../contexts/user/UserContext";
import { useContext } from "react";
import FadeSpinner from "../components/feedback/FadeSpinner";
import { Helmet } from "react-helmet";
import { FiPlus, FiMessageCircle, FiHeart, FiSend, FiImage, FiThumbsUp, FiAlertCircle } from "react-icons/fi";
import { FaCar, FaMapMarkerAlt, FaMoneyBillWave } from "react-icons/fa";

const BuyerRequestsFeed = () => {
  const { displayName, dispatch } = useContext(UserContext);
  const isMounted = useRef(true);
  const navigate = useNavigate();
  const [showReplyInput, setShowReplyInput] = useState({});
  const [loading, setLoading] = useState(true);
  const [posts, setPosts] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [commentInput, setCommentInput] = useState({});
  const [uploading, setUploading] = useState(false);
  const [likedPosts, setLikedPosts] = useState({});
  const [likedComments, setLikedComments] = useState({});
  const [postLoading, setPostLoading] = useState(false);
  const [newPost, setNewPost] = useState({
    author: displayName,
    description: "",
    location: "",
    make: "",
    model: "",
    city: "",
    budget: "",
  });

  // Color mode values - moved to top level
  const bgColor = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const textColor = useColorModeValue("gray.600", "gray.300");
  const headingColor = useColorModeValue("gray.800", "white");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const commentBg = useColorModeValue("gray.50", "gray.700");
  const authorColor = useColorModeValue("blue.600", "blue.300");
  const replyAuthorColor = useColorModeValue("green.600", "green.300");
  const likeButtonColor = useColorModeValue("blue", "blue");

  useEffect(() => {
    if (!displayName) return;

    const fetchLikedPosts = async () => {
      const q = query(
        collection(db, "posts"),
        where("likedBy", "array-contains", displayName)
      );
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const likedPostIds = {};
        snapshot.docs.forEach((doc) => {
          likedPostIds[doc.id] = true;
        });
        setLikedPosts(likedPostIds);
      });

      return () => unsubscribe();
    };

    fetchLikedPosts();
  }, [displayName]);

  useEffect(() => {
    if (isMounted) {
      onAuthStateChanged(auth, (user) => {
        if (!user) {
          navigate("/sign-in");
        }
      });
    }

    return () => {
      isMounted.current = false;
    };
  }, [isMounted, navigate]);

  useEffect(() => {
    if (!displayName) return;

    const fetchLikedComments = async () => {
      const unsubscribe = onSnapshot(collection(db, "posts"), (snapshot) => {
        const likedCommentIds = {};

        snapshot.docs.forEach((doc) => {
          const post = doc.data();
          const postId = doc.id;

          post.comments?.forEach((comment, index) => {
            if (comment.likedBy?.includes(displayName)) {
              likedCommentIds[`${postId}_${index}`] = true;
            }
          });
        });

        setLikedComments(likedCommentIds);
      });

      return () => unsubscribe();
    };

    fetchLikedComments();
  }, [displayName]);

  const handleImageUpload = async (e, postId) => {
    if (!e.target.files[0]) return;

    const file = e.target.files[0];
    const storageRef = ref(storage, `commentImages/${postId}/${file.name}`);
    setUploading(true);

    try {
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      setCommentInput((prev) => ({
        ...prev,
        [postId]: {
          ...prev[postId],
          imageUrl: downloadURL,
        },
      }));
      setUploading(false);
    } catch (error) {
      console.error("Error uploading image: ", error);
      setUploading(false);
    }
  };

  useEffect(() => {
    onAuthStateChanged(auth, (user) => {
      if (user) {
        dispatch({ type: "SET_USER", payload: user.displayName });
      }
    });
    const postsRef = collection(db, "posts");
    const q = query(postsRef, orderBy("timestamp", "desc"));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const updatedPosts = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setPosts(updatedPosts);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [dispatch]);

  const handleToggleLikeComment = async (postId, commentIndex) => {
    const postRef = doc(db, "posts", postId);
    const userId = displayName;

    try {
      const postSnapshot = await getDoc(postRef);
      if (postSnapshot.exists()) {
        const post = postSnapshot.data();
        const comments = post.comments || [];
        const comment = comments[commentIndex];

        if (!comment) return;

        const isLiked = comment.likedBy?.includes(userId);
        const updatedComments = [...comments];
        
        if (isLiked) {
          updatedComments[commentIndex] = {
            ...comment,
            likes: comment.likes - 1,
            likedBy: comment.likedBy.filter((id) => id !== userId),
          };
        } else {
          updatedComments[commentIndex] = {
            ...comment,
            likes: (comment.likes || 0) + 1,
            likedBy: [...(comment.likedBy || []), userId],
          };
        }

        await updateDoc(postRef, { comments: updatedComments });
        setLikedComments((prev) => ({
          ...prev,
          [`${postId}_${commentIndex}`]: !isLiked,
        }));
      }
    } catch (error) {
      console.error("Error toggling like for comment: ", error);
    }
  };

  const handleAddReply = async (postId, commentIndex, replyText) => {
    try {
      const postRef = doc(db, "posts", postId);
      const post = posts.find((post) => post.id === postId);
      const updatedComments = [...post.comments];
      const reply = {
        text: replyText,
        author: displayName,
        timestamp: new Date().toISOString(),
      };

      updatedComments[commentIndex].replies = updatedComments[commentIndex].replies || [];
      updatedComments[commentIndex].replies.push(reply);

      await updateDoc(postRef, { comments: updatedComments });
      setPosts((prevPosts) =>
        prevPosts.map((post) =>
          post.id === postId ? { ...post, comments: updatedComments } : post
        )
      );
      setShowReplyInput((prev) => ({ ...prev, [`${postId}_${commentIndex}`]: false }));
    } catch (error) {
      console.error("Error adding reply: ", error);
    }
  };

  const handleSavePost = async () => {
    setPostLoading(true);
    if (!newPost.description || !newPost.location || !newPost.make || !newPost.model || !newPost.city) {
      alert("All fields are required!");
      setPostLoading(false);
      return;
    }

    const postToSave = {
      ...newPost,
      author: displayName,
      likes: 0,
      comments: [],
      timestamp: serverTimestamp(),
    };

    try {
      const docRef = await addDoc(collection(db, "posts"), postToSave);
      setPosts([{ id: docRef.id, ...postToSave }, ...posts]);
      setNewPost({
        author: displayName,
        description: "",
        location: "",
        make: "",
        model: "",
        city: "",
        budget: "",
      });
      setModalOpen(false);
    } catch (error) {
      console.error("Error adding post: ", error);
    }
    setPostLoading(false);
  };

  const toggleCommentsVisibility = (postId) => {
    setPosts((prevPosts) =>
      prevPosts.map((post) =>
        post.id === postId ? { ...post, showComments: !post.showComments } : post
      )
    );
  };

  const handleToggleLike = async (postId) => {
    const postRef = doc(db, "posts", postId);
    const userId = displayName;

    try {
      const postSnapshot = await getDoc(postRef);
      if (postSnapshot.exists()) {
        const post = postSnapshot.data();
        const isLiked = post.likedBy?.includes(userId);

        if (isLiked) {
          await updateDoc(postRef, {
            likes: increment(-1),
            likedBy: arrayRemove(userId),
          });
        } else {
          await updateDoc(postRef, {
            likes: increment(1),
            likedBy: arrayUnion(userId),
          });
        }

        setLikedPosts((prev) => ({
          ...prev,
          [postId]: !isLiked,
        }));
      }
    } catch (error) {
      console.error("Error toggling like: ", error);
    }
  };

  const handleAddComment = async (postId) => {
    const commentData = commentInput[postId] || {};
    if (!commentData.text?.trim() && !commentData.imageUrl) {
      alert("Please add text or upload an image!");
      return;
    }

    const postRef = doc(db, "posts", postId);
    const updatedPost = posts.find((post) => post.id === postId);
    const updatedComments = [
      ...(updatedPost.comments || []),
      {
        author: displayName,
        text: commentData.text || "",
        imageUrl: commentData.imageUrl || null,
        timestamp: new Date().toISOString(),
        likes: 0,
        likedBy: [],
        replies: [],
      },
    ];

    try {
      await updateDoc(postRef, { comments: updatedComments });
      setPosts((prevPosts) =>
        prevPosts.map((post) =>
          post.id === postId ? { ...post, comments: updatedComments } : post
        )
      );
      setCommentInput((prev) => ({
        ...prev,
        [postId]: { text: "", imageUrl: null },
      }));
    } catch (error) {
      console.error("Error adding comment: ", error);
    }
  };

  if (loading) {
    return <FadeSpinner />;
  }

  return (
    <Box minH="100vh" bg={bgColor} py={8}>
      <Helmet>
        <title>Feed | NCPWheels</title>
        <meta name="description" content="Post your requirements or find the best buyers for your car" />
      </Helmet>

      <Container maxW="1200px" mx="auto" px={4}>
        {/* Header Section */}
        <VStack spacing={4} mb={8} textAlign="center">
          <Heading
            as="h1"
            fontSize={{ base: "3xl", md: "4xl" }}
            bgGradient="linear(to-r, blue.600, purple.600)"
            bgClip="text"
          >
            Buyer Requests
          </Heading>
          <Text color={textColor} fontSize="lg">
            Connect with buyers and sellers in your area
          </Text>
        </VStack>

        {/* Create Post Button */}
        <Box textAlign="center" mb={8}>
          <Button
            onClick={() => setModalOpen(true)}
            size="lg"
            colorScheme="blue"
            leftIcon={<Icon as={FiPlus} />}
            _hover={{
              transform: "translateY(-2px)",
              boxShadow: "xl"
            }}
            transition="all 0.3s"
          >
            Create New Buyer Request
          </Button>
        </Box>

        {/* Modal for creating a new post */}
        {modalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center px-4 z-[1400]">
            <div className="bg-white p-6 rounded-lg shadow-2xl w-full max-w-3xl max-h-screen overflow-y-auto">
              <h2 className="text-2xl font-semibold mb-4 text-gray-800">
                Create a New Buyer Request
              </h2>

              <div className="space-y-5">
                <div>
                  <label htmlFor="description" className="block text-gray-800 font-medium mb-1">
                    Description *
                  </label>
                  <textarea
                    id="description"
                    placeholder="Add description... e.g., Looking for a second-hand sedan."
                    className="w-full p-3 border border-gray-600 rounded-md placeholder-gray-500 text-gray-800"
                    rows={2}
                    value={newPost.description || ""}
                    onChange={(e) => setNewPost({ ...newPost, description: e.target.value })}
                  />
                </div>

                <div>
                  <label htmlFor="location" className="block text-gray-800 font-medium mb-1">
                    Location *
                  </label>
                  <input
                    id="location"
                    type="text"
                    placeholder="e.g., Islamabad, Pakistan"
                    value={newPost.location}
                    onChange={(e) => setNewPost({ ...newPost, location: e.target.value })}
                    className="w-full p-3 border border-gray-600 rounded-md placeholder-gray-500 text-gray-800"
                  />
                </div>

                <div>
                  <label htmlFor="make" className="block text-gray-800 font-medium mb-1">
                    Make *
                  </label>
                  <input
                    id="make"
                    type="text"
                    placeholder="e.g., Toyota, Honda"
                    value={newPost.make}
                    onChange={(e) => setNewPost({ ...newPost, make: e.target.value })}
                    className="w-full p-3 border border-gray-600 rounded-md placeholder-gray-500 text-gray-800"
                  />
                </div>

                <div>
                  <label htmlFor="model" className="block text-gray-800 font-medium mb-1">
                    Model *
                  </label>
                  <input
                    id="model"
                    type="text"
                    placeholder="e.g., Corolla, Civic"
                    value={newPost.model}
                    onChange={(e) => setNewPost({ ...newPost, model: e.target.value })}
                    className="w-full p-3 border border-gray-600 rounded-md placeholder-gray-500 text-gray-800"
                  />
                </div>

                <div>
                  <label htmlFor="city" className="block text-gray-800 font-medium mb-1">
                    City *
                  </label>
                  <input
                    id="city"
                    type="text"
                    placeholder="e.g., Lahore, Karachi"
                    value={newPost.city}
                    onChange={(e) => setNewPost({ ...newPost, city: e.target.value })}
                    className="w-full p-3 border border-gray-600 rounded-md placeholder-gray-500 text-gray-800"
                  />
                </div>

                <div>
                  <label htmlFor="budget" className="block text-gray-800 font-medium mb-1">
                    Budget
                  </label>
                  <input
                    id="budget"
                    type="text"
                    placeholder="e.g., 1,000,000 PKR"
                    value={newPost.budget || ""}
                    onChange={(e) => setNewPost({ ...newPost, budget: e.target.value })}
                    className="w-full p-3 border border-gray-600 rounded-md placeholder-gray-500 text-gray-800"
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-4">
                <button
                  onClick={handleSavePost}
                  className="bg-green-700 text-white py-2 px-5 rounded-md hover:bg-green-800 transition"
                >
                  {postLoading ? "Posting..." : "Post"}
                </button>
                <button
                  onClick={() => setModalOpen(false)}
                  className="bg-red-700 text-white py-2 px-5 rounded-md hover:bg-red-800 transition"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Feed or Empty State */}
        {posts.length === 0 ? (
          <VStack
            spacing={6}
            py={16}
            px={8}
            bg={cardBg}
            borderRadius="2xl"
            border="1px solid"
            borderColor={borderColor}
            textAlign="center"
          >
            <Box
              w={20}
              h={20}
              borderRadius="full"
              bg="blue.50"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Icon as={FiAlertCircle} w={10} h={10} color="blue.500" />
            </Box>
            <Heading as="h3" size="lg" color={headingColor}>
              No Buyer Requests Yet
            </Heading>
            <Text color={textColor} maxW="400px">
              Be the first to create a buyer request and connect with sellers in your area.
            </Text>
            <Button
              onClick={() => setModalOpen(true)}
              size="lg"
              colorScheme="blue"
              leftIcon={<Icon as={FiPlus} />}
              mt={4}
            >
              Create Your First Request
            </Button>
          </VStack>
        ) : (
          <VStack spacing={6} align="stretch">
            {posts.map((post) => (
              <Box
                key={post.id}
                bg={cardBg}
                borderRadius="xl"
                p={6}
                border="1px solid"
                borderColor={borderColor}
                shadow="sm"
                transition="all 0.3s"
                _hover={{ shadow: "md", transform: "translateY(-2px)" }}
              >
                {/* Author Section */}
                <Flex mb={4} align="center">
                  <Avatar size="md" name={post.author} />
                  <Box ml={3}>
                    <Text fontWeight="bold" color={headingColor}>
                      {post.author}
                    </Text>
                    <Text fontSize="xs" color={textColor}>
                      {post.timestamp?.toDate?.().toLocaleString() || "Just now"}
                    </Text>
                  </Box>
                </Flex>

                {/* Description */}
                <Text fontSize="lg" fontWeight="medium" mb={4} color={headingColor}>
                  {post.description}
                </Text>

                {/* Details Grid */}
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} mb={4}>
                  <HStack spacing={2}>
                    <Icon as={FaMapMarkerAlt} color="blue.500" boxSize={4} />
                    <Text fontSize="sm" color={textColor}>
                      <strong>Location:</strong> {post.location}
                    </Text>
                  </HStack>
                  <HStack spacing={2}>
                    <Icon as={FaCar} color="blue.500" boxSize={4} />
                    <Text fontSize="sm" color={textColor}>
                      <strong>Make:</strong> {post.make}
                    </Text>
                  </HStack>
                  <HStack spacing={2}>
                    <Icon as={FaCar} color="blue.500" boxSize={4} />
                    <Text fontSize="sm" color={textColor}>
                      <strong>Model:</strong> {post.model}
                    </Text>
                  </HStack>
                  <HStack spacing={2}>
                    <Icon as={FaMapMarkerAlt} color="blue.500" boxSize={4} />
                    <Text fontSize="sm" color={textColor}>
                      <strong>City:</strong> {post.city}
                    </Text>
                  </HStack>
                  {post.budget && (
                    <HStack spacing={2}>
                      <Icon as={FaMoneyBillWave} color="green.500" boxSize={4} />
                      <Text fontSize="sm" color={textColor}>
                        <strong>Budget:</strong> {post.budget}
                      </Text>
                    </HStack>
                  )}
                </SimpleGrid>

                {/* Likes and Comments Count */}
                <Flex justify="space-between" align="center" mb={3} pt={3} borderTop="1px solid" borderColor={borderColor}>
                  <HStack spacing={4}>
                    <HStack spacing={1}>
                      <Icon as={FiHeart} color={likedPosts[post.id] ? "red.500" : "gray.400"} />
                      <Text fontSize="sm" color={textColor}>{post.likes || 0} Likes</Text>
                    </HStack>
                    <HStack spacing={1}>
                      <Icon as={FiMessageCircle} color="gray.400" />
                      <Text fontSize="sm" color={textColor}>{post.comments?.length || 0} Comments</Text>
                    </HStack>
                  </HStack>
                </Flex>

                {/* Action Buttons */}
                <Flex gap={4} mb={4}>
                  <Button
                    size="sm"
                    variant="ghost"
                    colorScheme={likedPosts[post.id] ? "blue" : "gray"}
                    leftIcon={<Icon as={FiThumbsUp} />}
                    onClick={() => handleToggleLike(post.id)}
                    isDisabled={likedPosts[post.id]}
                  >
                    {likedPosts[post.id] ? "Liked" : "Like"}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    colorScheme="blue"
                    leftIcon={<Icon as={FiMessageCircle} />}
                    onClick={() => toggleCommentsVisibility(post.id)}
                  >
                    {post.showComments ? "Hide Comments" : "Comment"}
                  </Button>
                </Flex>

                {/* Comments Section */}
                {post.showComments && (
                  <Box mt={4}>
                    <Heading as="h4" size="sm" mb={3}>Comments</Heading>
                    
                    {post.comments?.length === 0 ? (
                      <Text fontSize="sm" color={textColor} textAlign="center" py={4}>
                        No comments yet. Be the first to comment!
                      </Text>
                    ) : (
                      <VStack spacing={3} align="stretch">
                        {post.comments?.map((comment, index) => (
                          <Box key={index} bg={commentBg} p={3} borderRadius="lg">
                            <Flex justify="space-between" align="start" mb={2}>
                              <Text fontWeight="bold" fontSize="sm" color={authorColor}>
                                {comment.author}
                              </Text>
                              <Text fontSize="xs" color={textColor}>
                                {new Date(comment.timestamp).toLocaleString()}
                              </Text>
                            </Flex>
                            
                            {comment.imageUrl && (
                              <Image
                                src={comment.imageUrl}
                                alt="Comment Attachment"
                                maxH="200px"
                                objectFit="cover"
                                borderRadius="md"
                                mb={2}
                              />
                            )}
                            
                            <Text fontSize="sm" mb={2}>{comment.text}</Text>
                            
                            <Flex gap={3} mt={2}>
                              <Button
                                size="xs"
                                variant="ghost"
                                colorScheme={likedComments[`${post.id}_${index}`] ? "blue" : "gray"}
                                leftIcon={<Icon as={FiThumbsUp} />}
                                onClick={() => handleToggleLikeComment(post.id, index)}
                              >
                                {likedComments[`${post.id}_${index}`] ? "Liked" : "Like"} ({comment.likes || 0})
                              </Button>
                              <Button
                                size="xs"
                                variant="ghost"
                                colorScheme="green"
                                onClick={() => setShowReplyInput(prev => ({ ...prev, [`${post.id}_${index}`]: !prev[`${post.id}_${index}`] }))}
                              >
                                Reply
                              </Button>
                            </Flex>

                            {/* Reply Input */}
                            {showReplyInput[`${post.id}_${index}`] && (
                              <Flex mt={2} gap={2}>
                                <input
                                  type="text"
                                  placeholder="Write a reply..."
                                  className="flex-1 p-2 border rounded-md text-sm"
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" && e.target.value.trim() !== "") {
                                      handleAddReply(post.id, index, e.target.value.trim());
                                      e.target.value = "";
                                    }
                                  }}
                                />
                              </Flex>
                            )}

                            {/* Replies */}
                            {comment.replies?.length > 0 && (
                              <Box mt={2} pl={4} borderLeft="2px solid" borderColor={borderColor}>
                                {comment.replies.map((reply, replyIndex) => (
                                  <Box key={replyIndex} mt={2}>
                                    <Flex justify="space-between" align="start">
                                      <Text fontWeight="bold" fontSize="xs" color={replyAuthorColor}>
                                        {reply.author}
                                      </Text>
                                      <Text fontSize="xs" color={textColor}>
                                        {new Date(reply.timestamp).toLocaleString()}
                                      </Text>
                                    </Flex>
                                    <Text fontSize="sm">{reply.text}</Text>
                                  </Box>
                                ))}
                              </Box>
                            )}
                          </Box>
                        ))}
                      </VStack>
                    )}

                    {/* Add Comment Form */}
                    <Box mt={4}>
                      <textarea
                        placeholder="Add a comment..."
                        className="w-full p-2 border rounded-md mb-2"
                        rows={2}
                        value={commentInput[post.id]?.text || ""}
                        onChange={(e) =>
                          setCommentInput({
                            ...commentInput,
                            [post.id]: {
                              ...commentInput[post.id],
                              text: e.target.value,
                            },
                          })
                        }
                      />
                      <Flex direction={{ base: "column", sm: "row" }} justify="space-between" align="center" gap={3}>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleImageUpload(e, post.id)}
                          className="text-sm"
                        />
                        <Button
                          size="sm"
                          colorScheme="green"
                          rightIcon={<Icon as={FiSend} />}
                          onClick={() => handleAddComment(post.id)}
                        >
                          Post Comment
                        </Button>
                      </Flex>
                    </Box>
                  </Box>
                )}
              </Box>
            ))}
          </VStack>
        )}
      </Container>
    </Box>
  );
};

export default BuyerRequestsFeed;